import { Box, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import { IssueReportRecord } from '../../redux/features/issueReport/issueReport.props';
import { UNCLASSIFIED_ISSUE_TYPE } from '../../constants/issueTypes';
import PriorFlagsTooltip from './PriorFlagsTooltip';
import { ISSUE_TOOLTIP_Z_INDEX } from './issueReportLayers';

/**
 * HITL-1501: what the agent has already flagged this session, on one line under the
 * panel title.
 *
 * Nitin's observation: a flag is filed, the panel closes, and the record leaves the
 * agent's view entirely — so an agent who is not sure whether a press registered
 * presses again. Commit 2 made the extra press harmless; this is what makes them not
 * want to press.
 *
 * ## Why one line and not a list
 *
 * The panel must never scroll — a value hidden below the fold is the exact failure
 * the Slack dropdown had, and it is the reason this panel exists. A row per report
 * costs ~4x the height and grows with the session; this is fixed at one line however
 * many are filed. Nothing is lost, because the tooltip carries the complete record.
 *
 * ## Why the whole line is the hover target
 *
 * Not each type name. Four separate hovers to assemble one answer is worse than none
 * with a car at the speaker.
 *
 * ## Why hovering does NOT pause the countdown
 *
 * The panel already refused to pause on FOCUS for this reason — it fires too easily,
 * and a mouse resting on the line would hold the panel open to MAX_FLAG_WINDOW_MS.
 * Typing stays the only thing that pauses. The consequence is that the panel can
 * close while the agent is still reading, which is why the Issue button carries the
 * same tooltip: the history is reachable at any point in the session, with no flag
 * open.
 */

/** Types shown inline before the overflow count. Three fits without wrapping. */
export const PRIOR_FLAGS_INLINE_LIMIT = 3;

interface PriorFlagsLineProps {
  /** This session's earlier reports, newest first, active one already excluded. */
  reports: IssueReportRecord[];
}

const PriorFlagsLine = ({ reports }: PriorFlagsLineProps) => {
  // Stamped when the tooltip opens, not on every render: the ages must not shift
  // under the agent's eyes while they read, and a render-time Date.now() would also
  // make this component untestable without fake timers.
  const [openedAtMs, setOpenedAtMs] = useState<number | null>(null);

  if (!reports.length) return null;

  const shown = reports.slice(0, PRIOR_FLAGS_INLINE_LIMIT);
  const overflow = reports.length - shown.length;

  return (
    <Tooltip
      arrow
      // TOP, not the default bottom: below would cover the detail field and the
      // first chip rows — the two things the agent opened the panel to use.
      placement="top-start"
      // INTERACTIVE (MUI's default, stated here because it is a decision): this
      // tooltip is several rows of text to read, so the agent must be able to move
      // the pointer onto it without it closing under them. The Issue button's
      // tooltip disables interactivity for the opposite reason — it sits over the
      // action bar and would block the controls beneath it.
      disableInteractive={false}
      onOpen={() => setOpenedAtMs(Date.now())}
      title={
        <PriorFlagsTooltip reports={reports} nowMs={openedAtMs ?? Date.now()} />
      }
      componentsProps={{
        // On the POPPER, not the tooltip: the z-index has to be on the portalled
        // root, which is what MUI positions. Without it this opens BEHIND the panel
        // it belongs to — see issueReportLayers.
        // `as never` for the same reason the tooltip entry below needs it: MUI
        // types this slot as Partial<PopperProps>, which has no `sx`. It is
        // forwarded to a styled Popper at runtime, so `sx` does apply.
        popper: { sx: { zIndex: ISSUE_TOOLTIP_Z_INDEX } } as never,
        tooltip: {
          // The tooltip is PORTALLED, so it sits outside the panel's DOM subtree and
          // does not inherit the panel's data-issue-report marker. The interception
          // guards match with closest() from the event target, so without its own
          // marker a mousedown inside the tooltip reads as taking over the order.
          'data-issue-report': 'true',
          sx: { maxWidth: 420, px: 1.25, py: 1 },
        } as never,
      }}
    >
      <Box
        data-testid="prior-flags-line"
        sx={{
          mt: '7px',
          ml: '-4px',
          px: '4px',
          py: '1px',
          width: 'fit-content',
          maxWidth: '100%',
          borderRadius: '3px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          gap: '0 6px',
          fontSize: '0.8125rem',
          lineHeight: 1.45,
          // `help` rather than `pointer`: there is nothing to click.
          cursor: 'help',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.08)',
            textDecoration: 'underline dotted rgba(255,255,255,0.4)',
            textUnderlineOffset: '3px',
          },
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: '0.625rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.45)',
          }}
        >
          Earlier this session
        </Box>

        {shown.map((report, index) => (
          <React.Fragment key={report.issue_id}>
            {index > 0 && (
              <Box component="span" sx={{ color: 'rgba(255,255,255,0.32)' }}>
                ·
              </Box>
            )}
            <Box component="span" sx={{ color: 'rgba(255,255,255,0.86)' }}>
              {report.issue_type ?? UNCLASSIFIED_ISSUE_TYPE}
            </Box>
          </React.Fragment>
        ))}

        {overflow > 0 && (
          <Box component="span" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            +{overflow} more
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default PriorFlagsLine;
